import type { ControlState } from "./controls";
import type { SimEvent } from "./events";
import type { WorldState } from "./state";
import { applyCollisions } from "./systems/collision";
import { applyExpiry } from "./systems/expiry";
import { applyFuelStations } from "./systems/fuel-stations";
import { applyGravity } from "./systems/gravity";
import { tickEntities } from "./systems/movement";
import { applyObjectives } from "./systems/objectives";
import { applyControls } from "./systems/ships";
import {
	performInsertions,
	performRemovals,
	spawnAsteroids,
	spawnPickups,
	tickPlayers,
} from "./systems/spawning";
import { applyWrap } from "./systems/wrap";

/**
 * Advances the world by one fixed tick (15 ms), mutating it in place, and returns what
 * happened. `controls[i]` is player i's input; missing entries mean "no input".
 *
 * Order follows the original game loop (Game::run → PlayState::tick → World::tick →
 * WorldObjectsManager::tick → strategies in Engine::init order → player tickables).
 */
export function step(world: WorldState, controls: readonly ControlState[]): SimEvent[] {
	const events: SimEvent[] = [];

	applyControls(world, controls);

	performRemovals(world, events);
	performInsertions(world, events);
	tickEntities(world, events);
	applyFuelStations(world);

	applyGravity(world);
	applyCollisions(world, events);
	applyWrap(world);
	applyObjectives(world, events);
	applyExpiry(world);
	spawnAsteroids(world);
	spawnPickups(world, events);

	world.tick++;
	tickPlayers(world, events);

	return events;
}
